import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/axiosConfig';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PeriodizationEditor from '../components/training/PeriodizationEditor';
import AssessmentSetManager from '../components/training/AssessmentSetManager';
import TrainingPlannerModal from '../components/trainingLog/TrainingPlannerModal';
import { Loader2, Plus, ArrowLeft, Settings2, Calendar, Sparkles, Layers } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const KIND_KEYS = {
  period: 'period',
  fixed_weeks: 'fixedWeeks',
  target_race: 'targetRace',
};

export default function TrainingPlanDesignPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const canPeriodizationCatalog = user?.permissions?.includes('periodization_catalog_manage');
  const [searchParams, setSearchParams] = useSearchParams();
  const focusBlockId = searchParams.get('blockId');

  const [loading, setLoading] = useState(true);
  const [blocks, setBlocks] = useState([]);
  const [tree, setTree] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [assessmentSets, setAssessmentSets] = useState([]);
  const [periodBlocks, setPeriodBlocks] = useState([]);
  const [creating, setCreating] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [structureSuggestion, setStructureSuggestion] = useState(null);
  const [createKind, setCreateKind] = useState('fixed_weeks');
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newScenarioId, setNewScenarioId] = useState('');
  const [newStartDate, setNewStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [newEndDate, setNewEndDate] = useState('');
  const [newRaceDate, setNewRaceDate] = useState('');
  const [newParentBlockId, setNewParentBlockId] = useState('');
  const [newAssessmentSetId, setNewAssessmentSetId] = useState('');
  const [newIsBaseContext, setNewIsBaseContext] = useState(true);
  const [managerOpen, setManagerOpen] = useState(false);
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [metaSaving, setMetaSaving] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const loadBlocks = useCallback(async () => {
    const res = await api.get('/api/training/periodization/blocks');
    const list = res.data.blocks || [];
    setBlocks(list);
    setPeriodBlocks(list.filter((b) => b.block_kind === 'period'));
    return list;
  }, []);

  const loadBlock = useCallback(async (id) => {
    if (!id) {
      setTree(null);
      return;
    }
    const res = await api.get(`/api/training/periodization/blocks/${id}`);
    setTree(res.data);
    setEditName(res.data.block?.name || '');
    setEditDescription(res.data.block?.description || '');
  }, []);

  const loadMeta = useCallback(async () => {
    const [scRes, asRes] = await Promise.all([
      api.get('/api/training/scenarios'),
      api.get('/api/training/periodization/assessment-sets'),
    ]);
    const mine = scRes.data.scenarios || [];
    setScenarios(mine);
    if (mine.length && !newScenarioId) setNewScenarioId(String(mine[0].id));
    setAssessmentSets(asRes.data.assessment_sets || []);
  }, [newScenarioId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const list = await loadBlocks();
        await loadMeta();
        const id = focusBlockId || (list[0] ? String(list[0].id) : null);
        if (id) await loadBlock(id);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [focusBlockId, loadBlocks, loadBlock, loadMeta]);

  const selectBlock = async (id) => {
    setSearchParams(id ? { blockId: id } : {});
    await loadBlock(id);
  };

  const suggestStructure = async () => {
    setSuggesting(true);
    try {
      const res = await api.post('/api/training/periodization/blocks/suggest-structure', {
        block_kind: createKind,
        start_date: newStartDate,
        end_date: newEndDate,
        target_race_date: newRaceDate,
        scenario_id: newScenarioId ? parseInt(newScenarioId, 10) : undefined,
        parent_block_id: newParentBlockId ? parseInt(newParentBlockId, 10) : undefined,
      });
      const data = res.data;
      setStructureSuggestion(data);
      if (data.start_date) setNewStartDate(data.start_date);
      if (data.end_date) setNewEndDate(data.end_date);
      if (data.target_race_date) setNewRaceDate(data.target_race_date);
    } catch (e) {
      console.error(e);
      setStructureSuggestion(null);
    } finally {
      setSuggesting(false);
    }
  };

  const createBlock = async () => {
    setCreating(true);
    try {
      const body = {
        block_kind: createKind,
        name: newName || undefined,
        description: newDescription || undefined,
        assessment_set_id: newAssessmentSetId ? parseInt(newAssessmentSetId, 10) : undefined,
        status: 'active',
        parent_block_id: newParentBlockId ? parseInt(newParentBlockId, 10) : undefined,
        is_base_context: createKind === 'period' ? newIsBaseContext : undefined,
      };
      if (createKind === 'period') {
        body.start_date = newStartDate;
        body.end_date = newEndDate || newStartDate;
        body.microcycles_enabled = false;
      } else if (createKind === 'target_race') {
        body.target_race_date = newRaceDate;
        body.scenario_id = newScenarioId ? parseInt(newScenarioId, 10) : undefined;
      } else {
        body.start_date = newStartDate;
        body.scenario_id = newScenarioId ? parseInt(newScenarioId, 10) : undefined;
      }
      const res = await api.post('/api/training/periodization/blocks', body);
      await loadBlocks();
      setTree(res.data);
      setSearchParams({ blockId: String(res.data.block.id) });
    } catch (e) {
      console.error(e);
    } finally {
      setCreating(false);
    }
  };

  const saveBlockMeta = async () => {
    if (!tree?.block?.id) return;
    setMetaSaving(true);
    try {
      const res = await api.put(`/api/training/periodization/blocks/${tree.block.id}`, {
        name: editName,
        description: editDescription,
      });
      setTree(res.data);
      await loadBlocks();
    } catch (e) {
      console.error(e);
    } finally {
      setMetaSaving(false);
    }
  };

  if (loading) {
    return (
      <div className='flex min-h-[40vh] items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
      </div>
    );
  }

  return (
    <div className='mx-auto max-w-6xl space-y-6 p-4 md:p-6'>
      <div className='flex flex-wrap items-center gap-3'>
        <Button variant='ghost' size='sm' asChild>
          <Link to='/dashboard?context=athlete_training_plans'>
            <ArrowLeft className='mr-1 h-4 w-4' />
            {t('periodization.backToPlans')}
          </Link>
        </Button>
        <h1 className='text-xl font-semibold'>{t('periodization.designTitle')}</h1>
        <Button
          type='button'
          size='sm'
          variant='outline'
          className='gap-1'
          onClick={() => setPlannerOpen(true)}
        >
          <Calendar className='h-4 w-4' />
          {t('trainingLog.plannerTitle')}
        </Button>
        <Button type='button' size='sm' variant='ghost' className='gap-1 text-muted-foreground' asChild>
          <Link to={tree?.block?.id ? `/training-plan/calendar?blockId=${tree.block.id}` : '/training-plan/calendar'}>
            <Calendar className='h-4 w-4' />
            {t('periodization.calendar.title')}
          </Link>
        </Button>
        <Button type='button' size='sm' variant='ghost' className='gap-1 text-muted-foreground' asChild>
          <Link to='/training-plan/attribute-analytics'>
            {t('periodization.analytics.title')}
          </Link>
        </Button>
        {canPeriodizationCatalog ? (
          <Button type='button' size='sm' variant='outline' className='gap-1' asChild>
            <Link to='/admin/periodization-catalog'>
              <Layers className='h-4 w-4' />
              {t('periodization.admin.openCatalog')}
            </Link>
          </Button>
        ) : null}
        <Button type='button' size='sm' variant='outline' className='ml-auto gap-1' onClick={() => setManagerOpen(true)}>
          <Settings2 className='h-4 w-4' />
          {t('periodization.manageAssessment')}
        </Button>
      </div>

      <AssessmentSetManager open={managerOpen} onOpenChange={setManagerOpen} onChanged={loadMeta} />
      <TrainingPlannerModal open={plannerOpen} onOpenChange={setPlannerOpen} />

      <div className='grid gap-6 lg:grid-cols-[260px_1fr]'>
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm'>{t('periodization.blocks')}</CardTitle>
          </CardHeader>
          <CardContent className='space-y-2'>
            {blocks.map((b) => (
              <button
                key={b.id}
                type='button'
                onClick={() => selectBlock(b.id)}
                className={`w-full rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                  tree?.block?.id === b.id
                    ? 'border-primary bg-accent text-accent-foreground'
                    : 'border-border hover:bg-muted/50'
                }`}
              >
                <div className='flex items-center gap-1'>
                  <span className='font-medium truncate flex-1'>{b.name}</span>
                  <Badge variant='secondary' className='text-[9px] shrink-0'>
                    {t(`periodization.blockKinds.${KIND_KEYS[b.block_kind] || 'fixedWeeks'}`)}
                  </Badge>
                </div>
                <div className='text-muted-foreground'>
                  {b.start_date} → {b.end_date}
                </div>
              </button>
            ))}

            <div className='pt-3 border-t border-border space-y-2'>
              <p className='text-xs font-medium'>{t('periodization.newBlock')}</p>
              <Tabs value={createKind} onValueChange={setCreateKind}>
                <TabsList className='w-full h-8'>
                  <TabsTrigger value='period' className='text-[10px] px-1'>{t('periodization.blockKinds.period')}</TabsTrigger>
                  <TabsTrigger value='fixed_weeks' className='text-[10px] px-1'>{t('periodization.blockKinds.fixedWeeks')}</TabsTrigger>
                  <TabsTrigger value='target_race' className='text-[10px] px-1'>{t('periodization.blockKinds.targetRace')}</TabsTrigger>
                </TabsList>
              </Tabs>
              <div className='space-y-1.5'>
                <Label className='text-xs'>{t('periodization.blockName')}</Label>
                <Input className='h-8 text-xs' value={newName} onChange={(e) => setNewName(e.target.value)} />
              </div>
              <div className='space-y-1.5'>
                <Label className='text-xs'>{t('periodization.blockDescription')}</Label>
                <Textarea className='text-xs min-h-[60px]' value={newDescription} onChange={(e) => setNewDescription(e.target.value)} />
              </div>
              {createKind === 'period' && (
                <>
                  <div className='space-y-1.5'>
                    <Label className='text-xs'>{t('periodization.startDate')}</Label>
                    <Input type='date' className='h-8 text-xs' value={newStartDate} onChange={(e) => setNewStartDate(e.target.value)} />
                  </div>
                  <div className='space-y-1.5'>
                    <Label className='text-xs'>{t('periodization.endDate')}</Label>
                    <Input type='date' className='h-8 text-xs' value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)} />
                  </div>
                  <div className='flex items-center justify-between gap-2'>
                    <Label className='text-xs'>{t('periodization.baseContext')}</Label>
                    <Switch checked={newIsBaseContext} onCheckedChange={setNewIsBaseContext} />
                  </div>
                </>
              )}
              {createKind === 'fixed_weeks' && (
                <>
                  <div className='space-y-1.5'>
                    <Label className='text-xs'>{t('periodization.scenario')}</Label>
                    <Select value={newScenarioId} onValueChange={setNewScenarioId}>
                      <SelectTrigger className='h-8 text-xs'><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {scenarios.map((s) => (
                          <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className='space-y-1.5'>
                    <Label className='text-xs'>{t('periodization.startDate')}</Label>
                    <Input type='date' className='h-8 text-xs' value={newStartDate} onChange={(e) => setNewStartDate(e.target.value)} />
                  </div>
                </>
              )}
              {createKind === 'target_race' && (
                <>
                  <div className='space-y-1.5'>
                    <Label className='text-xs'>{t('periodization.raceDate')}</Label>
                    <Input type='date' className='h-8 text-xs' value={newRaceDate} onChange={(e) => setNewRaceDate(e.target.value)} />
                  </div>
                  <div className='space-y-1.5'>
                    <Label className='text-xs'>{t('periodization.scenario')}</Label>
                    <Select value={newScenarioId} onValueChange={setNewScenarioId}>
                      <SelectTrigger className='h-8 text-xs'><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {scenarios.map((s) => (
                          <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              {createKind !== 'period' && periodBlocks.length > 0 && (
                <div className='space-y-1.5'>
                  <Label className='text-xs'>{t('periodization.parentPeriod')}</Label>
                  <Select value={newParentBlockId || '_none'} onValueChange={(v) => setNewParentBlockId(v === '_none' ? '' : v)}>
                    <SelectTrigger className='h-8 text-xs'><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value='_none'>{t('periodization.noParent')}</SelectItem>
                      {periodBlocks.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className='space-y-1.5'>
                <Label className='text-xs'>{t('periodization.assessmentSet')}</Label>
                <Select value={newAssessmentSetId || '_default'} onValueChange={(v) => setNewAssessmentSetId(v === '_default' ? '' : v)}>
                  <SelectTrigger className='h-8 text-xs'><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value='_default'>{t('periodization.defaultAssessmentSet')}</SelectItem>
                    {assessmentSets.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type='button' size='sm' variant='outline' className='w-full gap-1' onClick={suggestStructure} disabled={suggesting}>
                {suggesting ? <Loader2 className='h-4 w-4 animate-spin' /> : <Sparkles className='h-4 w-4' />}
                {t('periodization.suggestStructure')}
              </Button>
              {structureSuggestion?.phases?.length > 0 && (
                <div className='rounded-md border border-border bg-muted/40 p-2 text-xs space-y-1'>
                  {structureSuggestion.compressed ? (
                    <Badge variant='secondary' className='text-[10px]'>
                      {t('periodization.structureCompressed', {
                        weeks: structureSuggestion.duration_weeks,
                        templateWeeks: structureSuggestion.template_duration_weeks,
                      })}
                    </Badge>
                  ) : null}
                  {structureSuggestion.rationale ? (
                    <p className='text-muted-foreground'>{structureSuggestion.rationale}</p>
                  ) : null}
                  <p className='font-medium text-foreground'>{t('periodization.structurePreview')}</p>
                  <ul className='space-y-0.5 text-muted-foreground'>
                    {structureSuggestion.phases.map((p, i) => (
                      <li key={i}>
                        {p.name} ({p.phase_kind}) — {p.start_date} → {p.end_date}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <Button type='button' size='sm' className='w-full gap-1' onClick={createBlock} disabled={creating}>
                {creating ? <Loader2 className='h-4 w-4 animate-spin' /> : <Plus className='h-4 w-4' />}
                {t('periodization.createBlock')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className='min-w-0 space-y-4'>
          {tree ? (
            <>
              <Card>
                <CardContent className='pt-4 space-y-3'>
                  <div className='grid gap-2 sm:grid-cols-[1fr_auto]'>
                    <div>
                      <Label className='text-xs'>{t('periodization.blockName')}</Label>
                      <Input className='h-8 text-xs mt-1' value={editName} onChange={(e) => setEditName(e.target.value)} />
                    </div>
                    <div className='flex items-end'>
                      <Button type='button' size='sm' onClick={saveBlockMeta} disabled={metaSaving}>
                        {metaSaving ? <Loader2 className='h-4 w-4 animate-spin' /> : t('common.save')}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className='text-xs'>{t('periodization.blockDescription')}</Label>
                    <Textarea
                      className='text-xs mt-1 min-h-[72px]'
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                    />
                  </div>
                </CardContent>
              </Card>
              <PeriodizationEditor
                tree={tree}
                onUpdated={setTree}
                assessmentSets={assessmentSets}
                onOpenAssessmentManager={() => setManagerOpen(true)}
              />
            </>
          ) : (
            <p className='text-sm text-muted-foreground'>{t('periodization.selectOrCreate')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
